import { logWebEvent, db, FieldValue } from '../firebase.js';

// Fire-and-forget shareEvents doc — the same per-question share attribution
// iOS ShareTelemetry writes (2026-07-03, question-agent v1.9 feedback loop:
// per-question share counts teach the agent WHICH questions users share).
// Rules require userId == auth.uid + cardType + sharedAt via hasAll — extra
// fields like questionId are permitted without a rules change. Failure is
// swallowed: missing telemetry must never break a share.
function logShareEventDoc({ uid, cardType, questionId, completed }) {
  if (!uid) return;
  const doc = {
    userId: uid,
    cardType,
    sharedAt: FieldValue.serverTimestamp(),
    completed,
  };
  if (questionId) doc.questionId = questionId;
  db.collection('shareEvents')
    .add(doc)
    .catch(() => {
      /* best-effort telemetry */
    });
}

// Share a single question → the apex /q/<id> door (crawlable OG image, bounces to
// the play SPA; ?ref drains into referral attribution on a friend's signup) +
// claim the +6/day coin bonus. Extracted from Play.jsx's shareQuestion so the
// Results-tab SHARE QUESTION button reuses the EXACT same beat. The server
// enforces eligibility (today's six OR a question the caller voted on) + the
// 36/day cap + per-question idempotency, so calling on every share is safe —
// only a fresh, eligible share actually credits. Never throws.
//
// Returns { shared, usedClipboard, awarded, amount, reason }:
//   shared        — the share sheet completed (or the link was copied)
//   usedClipboard — fell back to clipboard copy (show a "copied" note)
//   awarded       — the +6 coin bonus credited this call
//   amount        — coins credited (6) when awarded
//   reason        — server reason when NOT awarded (e.g. already-claimed-for-question-today)
export async function shareQuestionLink({ questionId, questionText, uid, source = 'card', h2hInviteId = null, followHandle = null }) {
  // Results-tab shares (source starts with "results") tag the link ?src=results so
  // the /q unfurl shows the weighted consensus pick. When an h2hInviteId is passed
  // (the Today "Play with a Friend" flow), the link carries ?h2h=<id> so tapping it
  // creates a game — same question-image unfurl, game on the other end.
  const params = [];
  if (uid) params.push(`ref=${uid}`);
  if (h2hInviteId) params.push(`h2h=${h2hInviteId}`);
  // VP-2: an approved Predictor's share carries ?f=<handle> — tapping it leads
  // to their LEAGUE, not a head-to-head. Mutually exclusive with ?h2h= by
  // construction: the caller forks on identity and never mints an invite for a
  // Predictor. The handle rather than a token, because it's already public,
  // it's the same key /f/<handle> uses, and a legible URL is a better artifact
  // than an opaque one.
  if (followHandle) params.push(`f=${encodeURIComponent(followHandle)}`);
  if (typeof source === 'string' && source.startsWith('results')) params.push('src=results');
  const url = `https://predictsixapp.com/q/${questionId}${params.length ? `?${params.join('&')}` : ''}`;
  logWebEvent('web_question_shared', { questionId, source });

  let shared = false;
  let usedClipboard = false;
  try {
    if (navigator.share) {
      await navigator.share({ title: 'Predict Six', text: questionText || '', url });
      shared = true;
    } else if (navigator.clipboard) {
      // TEXT AND link (2026-08-05): this branch used to copy the bare url,
      // silently discarding the artifact the caller just composed — the
      // 🔒×6 locked-six line, the 🔔 verdict, the 🤔 open question. Every
      // desktop share pasted a naked URL with no ask. `shareStoryLink` and
      // `shareNightlyReceipt` already copy both; these doors now agree.
      await navigator.clipboard.writeText(
        questionText ? `${questionText}\n${url}` : url,
      );
      shared = true;
      usedClipboard = true;
    }
  } catch {
    /* user cancelled the share sheet, or unsupported → no share */
  }
  // Per-question share attribution (Firestore mirror of the GA4 event above,
  // so the admin/agent loop can read it without the BigQuery export).
  logShareEventDoc({
    uid,
    cardType: source && String(source).startsWith('results') ? 'question_results' : 'prediction',
    questionId,
    completed: shared,
  });
  // The +6-per-share economy was retired 2026-07-01 — shares are pure referral
  // links now (the sharer is paid by the +180 referral only when the recipient
  // actually plays), plus the h2h game-creation when an invite id is attached.
  return { shared, usedClipboard, awarded: false };
}
